import { Modal as MatModal } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { media } from 'src/assets/_responsive-mixin';
import styled from 'styled-components';
import { slideUp, wiggle } from '../Animations/KeyFrames';
import { ActionButton, PrimaryButton, TertiaryButton } from '../Button/Button';
import { Header1 } from '../Typography/Header1';

type Props = {
  header?: string;
  width?: number;
  open: boolean;
  confirmText?: string;
  showActions?: boolean;
  allowCommonExitStrategies?: boolean;
  padBody?: boolean;
  children: React.ReactNode | React.ReactNode[];
  onClose: () => void;
  onConfirm?: () => void;
};

export const Modal: React.FC<Props> = ({
  header,
  width,
  open,
  showActions,
  confirmText,
  allowCommonExitStrategies,
  padBody = true,
  children,
  onClose,
  onConfirm
}) => {
  const [wiggle, setWiggle] = useState<boolean>(false);

  const modal = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const handleOnClose = () => {
    if (allowCommonExitStrategies) {
      onClose();
    } else {
      onWiggle();
    }
  };

  const onWiggle = () => {
    setWiggle(true);
    setTimeout(() => setWiggle(false), 400);
  };

  return (
    <MatModal open={open} className="flex" onClose={handleOnClose}>
      <ModalWrapper
        ref={modal}
        className={wiggle ? 'wiggle' : ''}
        style={{
          maxWidth: width
        }}
      >
        <CloseButton onClick={onClose} icon="las la-times" />

        {header && (
          <ModalHeader>
            <Header1>{header}</Header1>
          </ModalHeader>
        )}

        <ModalBody className={!padBody ? 'p-0' : ''}>
          {children}

          {showActions && (
            <ModalFooter>
              <TertiaryButton label={t('cancel')} onClick={onClose} />
              <PrimaryButton
                label={confirmText ? confirmText : t('confirm')}
                onClick={onConfirm}
              />
            </ModalFooter>
          )}
        </ModalBody>
      </ModalWrapper>
    </MatModal>
  );
};

Modal.defaultProps = {
  width: 620,
  showActions: false,
  allowCommonExitStrategies: true
};

const ModalWrapper = styled.div`
  background-color: #fff;
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  max-height: 90%;
  width: 100%;
  margin: auto;
  overflow: hidden;
  border-radius: 8px;
  animation: ${slideUp} 300ms ease-out;

  &.wiggle {
    animation: ${slideUp} 300ms ease-out, ${wiggle} 400ms ease-out;
  }
`;

export const ModalHeader = styled.section`
  padding: 48px 48px 32px;
  border-bottom: 1px solid var(--primary-color20);
`;

export const ModalBody = styled.section`
  display: flex;
  flex-direction: column;
  overflow: auto;

  padding: 48px;

  ${media.xsmall`
		padding: 16px;
	`}

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ModalFooter = styled.footer`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 48px;
`;

const CloseButton = styled(ActionButton)`
  position: absolute;
  right: 2rem;
  top: 2rem;
  z-index: 500;
`;
